export type MoqInputs = {
  annualDemand: number;
  /** Annual holding cost as a percentage of unit cost, e.g. 25 for 25%. */
  holdingCostPercent: number;
  orderingCost: number;
  /** Supplier minimum. Null when the buyer has not been given one. */
  supplierMoq: number | null;
  unitCost: number;
};

export type MoqResult = {
  annualHoldingCost: number;
  annualOrderingCost: number;
  /** Cost of the goods themselves. Constant across order sizes. */
  annualPurchaseCost: number;
  /** Cash tied up in stock on average, at the recommended quantity. */
  averageInventoryValue: number;
  eoq: number;
  /** Ordering + holding cost if you could order exactly at the EOQ. */
  eoqAnnualCost: number;
  holdingCostPerUnit: number;
  /** Ordering + holding cost spread over each unit sold. Excludes the goods. */
  inventoryCostPerUnit: number;
  meetsMoq: boolean;
  /** Ordering + holding cost the supplier minimum adds over the EOQ. */
  moqPremium: number;
  moqPremiumPercent: number | null;
  orderCycleDays: number;
  ordersPerYear: number;
  recommendedOrderQty: number;
  /** Ordering + holding cost at the recommended quantity. Excludes goods. */
  totalAnnualCost: number;
  warnings: string[];
};

export const MOQ_ASSUMPTIONS = {
  /** Holding cost above this is worth a second look. */
  highHoldingCostPercent: 60,
  daysPerYear: 365,
} as const;

function asNonNegative(value: number) {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function roundCurrency(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function parseNumericInput(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

/**
 * Returns the reason the order quantity cannot be modelled, or null when it can.
 *
 * Every one of these is required: with a zero holding cost or zero demand the
 * EOQ formula has no finite solution, so silently defaulting would invent an
 * answer the buyer never entered.
 */
export function getMoqInputError(input: {
  annualDemand: number;
  holdingCostPercent: number;
  orderingCost: number;
  unitCost: number;
}): string | null {
  if (asNonNegative(input.unitCost) <= 0) {
    return 'Enter a unit cost to calculate the economic order quantity.';
  }

  if (asNonNegative(input.annualDemand) <= 0) {
    return 'Enter your annual demand in units.';
  }

  if (asNonNegative(input.orderingCost) <= 0) {
    return 'Enter the cost of placing one order.';
  }

  if (asNonNegative(input.holdingCostPercent) <= 0) {
    return 'Enter a holding cost above 0%, because it is what balances the EOQ.';
  }

  return null;
}

/** Annual ordering + holding cost of buying in batches of `orderQty`. */
function totalAnnualCost({
  annualDemand,
  holdingCostPerUnit,
  orderQty,
  orderingCost,
}: {
  annualDemand: number;
  holdingCostPerUnit: number;
  orderQty: number;
  orderingCost: number;
}) {
  if (orderQty <= 0) {
    return 0;
  }

  return (
    (annualDemand / orderQty) * orderingCost +
    (orderQty / 2) * holdingCostPerUnit
  );
}

export function calculateMoq(input: MoqInputs): MoqResult {
  const unitCost = asNonNegative(input.unitCost);
  const annualDemand = asNonNegative(input.annualDemand);
  const orderingCost = asNonNegative(input.orderingCost);
  const holdingCostPercent = asNonNegative(input.holdingCostPercent);
  const supplierMoq =
    input.supplierMoq === null ? 0 : asNonNegative(input.supplierMoq);

  const holdingCostPerUnit = unitCost * (holdingCostPercent / 100);

  const eoq =
    holdingCostPerUnit > 0 && annualDemand > 0 && orderingCost > 0
      ? Math.round(
          Math.sqrt((2 * annualDemand * orderingCost) / holdingCostPerUnit),
        )
      : 0;

  const recommendedOrderQty = Math.max(eoq, supplierMoq);
  const ordersPerYear =
    recommendedOrderQty > 0 ? annualDemand / recommendedOrderQty : 0;

  const annualOrderingCost = roundCurrency(ordersPerYear * orderingCost);
  const annualHoldingCost = roundCurrency(
    (recommendedOrderQty / 2) * holdingCostPerUnit,
  );
  const totalCost = roundCurrency(annualOrderingCost + annualHoldingCost);

  const eoqAnnualCost = roundCurrency(
    totalAnnualCost({
      annualDemand,
      holdingCostPerUnit,
      orderQty: eoq,
      orderingCost,
    }),
  );

  // The premium is the full cost difference, not just the extra holding cost:
  // ordering less often claws some of it back.
  const moqPremium =
    supplierMoq > eoq && eoq > 0
      ? Math.max(0, roundCurrency(totalCost - eoqAnnualCost))
      : 0;

  const moqPremiumPercent =
    moqPremium > 0 && eoqAnnualCost > 0
      ? Math.round((moqPremium / eoqAnnualCost) * 1000) / 10
      : null;

  const orderCycleDays =
    annualDemand > 0
      ? Math.round(
          (recommendedOrderQty / annualDemand) * MOQ_ASSUMPTIONS.daysPerYear,
        )
      : 0;

  const warnings: string[] = [];

  if (supplierMoq > 0 && supplierMoq > annualDemand) {
    warnings.push(
      'The supplier minimum is larger than a full year of demand. You would be holding more than 12 months of stock before the first reorder.',
    );
  } else if (orderCycleDays > MOQ_ASSUMPTIONS.daysPerYear / 2) {
    warnings.push(
      `At this quantity you would reorder roughly every ${orderCycleDays} days. Check the shelf life, cash flow, and obsolescence risk of holding that long.`,
    );
  }

  if (holdingCostPercent > MOQ_ASSUMPTIONS.highHoldingCostPercent) {
    warnings.push(
      `A ${holdingCostPercent}% holding cost is unusually high and pulls the EOQ down sharply. Most importers land between 20% and 30%.`,
    );
  }

  if (supplierMoq > 0 && eoq >= supplierMoq) {
    warnings.push(
      'Your EOQ already clears the supplier minimum, so the MOQ is not costing you anything here.',
    );
  }

  return {
    annualHoldingCost,
    annualOrderingCost,
    annualPurchaseCost: roundCurrency(annualDemand * unitCost),
    averageInventoryValue: roundCurrency((recommendedOrderQty / 2) * unitCost),
    eoq,
    eoqAnnualCost,
    holdingCostPerUnit: roundCurrency(holdingCostPerUnit),
    inventoryCostPerUnit:
      annualDemand > 0 ? roundCurrency(totalCost / annualDemand) : 0,
    meetsMoq: supplierMoq === 0 || eoq >= supplierMoq,
    moqPremium,
    moqPremiumPercent,
    orderCycleDays,
    ordersPerYear: Math.round(ordersPerYear * 10) / 10,
    recommendedOrderQty,
    totalAnnualCost: totalCost,
    warnings,
  };
}
